import { AxiosPromise } from 'axios';
import { ChangeEvent, RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';

import { coreLoginClient } from 'app/api/apiClient/generated/coreLoginClient';
import Button, { ButtonProps } from 'app/components/v2/components/Button';
import { TypeAheadOption } from 'app/components/v2/components/TypeAhead';
import { useAlertsDispatch } from 'app/state/AlertsContext/hooks';
import { handleErrors } from 'app/utils/lib/context';
import { downloadFile } from 'app/utils/lib/file';
import { convertCentsToDollars, formatCurrency } from 'app/utils/lib/number';
import { getNumberFromPrice, parseDateInput } from 'app/utils/lib/string';
import { isPasswordValid, StrengthGuide } from 'app/utils/lib/validators';

// Local Typings
export interface UseCheckboxProps {
  checked: boolean;
  onChange: () => void;
  reset: () => void;
}

export interface UseTextAreaProps {
  onChange: (evt: ChangeEvent<HTMLTextAreaElement>) => void;
  reset: () => void;
  value: string;
}
export interface UseTextFieldProps {
  clear: () => void;
  onChange: (evt: ChangeEvent<HTMLInputElement>) => void;
  reset: () => void;
  value: string;
}
interface OneTimeCodeFieldProps extends UseTextFieldProps {
  autoComplete: 'off' | 'one-time-code';
}
interface UsePasswordFieldOptions extends UseTextFieldOptions {
  withStrengthGuide?: boolean;
}
export interface UsePasswordFieldProps extends UseTextFieldProps {
  error: boolean;
  errorMessage: null | string;
  isValid: boolean;
  strengthGuide: StrengthGuide | undefined;
}
export interface UseMoneyFieldProps {
  onChange: (evt: ChangeEvent<HTMLInputElement>) => void;
  onReset: () => void;
  setValue: (value: string) => void;
  value: string;
  valueAsNumber: number;
}
export interface UseDownloadLinkProps {
  isLoading: boolean;
  onClick: () => void;
}
export interface UseTypeAheadProps {
  initialValue: string;
  inputValue: string;
  isSelected: boolean;
  onChange: (evt: ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
  onReset: () => void;
  onTypeAheadSelect: (option: TypeAheadOption) => void;
  selectedId: null | number | string;
  value: string;
}
export interface UseNumberOnlyFieldProps {
  onChange: (evt: ChangeEvent<HTMLInputElement>) => void;
  reset: () => void;
  value: string;
}
export interface UseSelectProps {
  onChange: (id: number | string) => void;
  reset: () => void;
  value: number | string;
}

type ScrollDirectionX = 'left' | 'right';
type ScrollDirectionY = 'down' | 'up';
type ScrollDirection = ScrollDirectionX | ScrollDirectionY;

interface ScrollCoordinates {
  x: number;
  y: number;
}

// Exports
export function useCheckbox(initialValue: boolean): UseCheckboxProps {
  const [value, setValue] = useState(initialValue);
  const handleChange = useCallback(
    () => {
      setValue(v => !v);
    },
    [],
  );

  const reset = useCallback(
    () => {
      setValue(initialValue);
    },
    [setValue, initialValue],
  );

  return useMemo(
    () => ({
      checked: value,
      onChange: handleChange,
      reset,
    }),
    [handleChange, reset, value],
  );
}

export function useDownloadLink({
  cb,
  downloadFileName,
}: {
  cb: () => AxiosPromise;
  downloadFileName: string;
}): UseDownloadLinkProps {
  const [isDownloading, setIsDownloading] = useState(false);

  const alertsDispatch = useAlertsDispatch();

  const handleClick = useCallback(
    async () => {
      try {
        setIsDownloading(true);

        await downloadFile(
          cb,
          downloadFileName,
        );
      } catch (error) {
        handleErrors({
          dispatch: alertsDispatch,
          errorObject: error as Error,
          expectedKeys: [],
        });
      } finally {
        setIsDownloading(false);
      }
    },
    [alertsDispatch, cb, downloadFileName],
  );

  return useMemo(
    () => ({
      isLoading: isDownloading,
      onClick: handleClick,
    }),
    [handleClick, isDownloading],
  );
}

export function useToggle(initialValue: boolean) {
  const [value, setValue] = useState(initialValue);

  const handleToggle = useCallback(
    () => {
      setValue(v => !v);
    },
    [],
  );

  useEffect(
    () => {
      setValue(initialValue);
    },
    [initialValue],
  );

  return useMemo(
    () => ({
      onError: handleToggle,
      onToggle: handleToggle,
      setValue,
      value,
    }),
    [handleToggle, value],
  );
}

export function useTypeAhead(
  initialValue: string, initialId: null | number | string,
): UseTypeAheadProps {
  const [value, setValue] = useState(initialValue);
  const [selectedId, setSelectedId] = useState<null | number | string>(initialId);
  const [inputValue, setInputValue] = useState(initialValue || '');
  const [isSelected, setIsSelected] = useState(!!initialValue);

  const handleChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      if (isSelected) {
        setIsSelected(false);
      }
      setInputValue(evt.target.value);
    },
    [isSelected],
  );

  const handleDeselect = useCallback(
    () => {
      setSelectedId(null);
      setValue('');
    },
    [],
  );

  const handleSelect = useCallback(
    (option: TypeAheadOption) => {
      setInputValue(option.label);
      setIsSelected(true);
      setValue(option.label);
      setSelectedId(option.id ?? null);
    },
    [],
  );

  const handleClear = useCallback(
    () => {
      setIsSelected(false);
      setInputValue('');
      handleDeselect();
    },
    [handleDeselect],
  );

  const handleReset = useCallback(
    () => {
      setValue(initialValue);
      setSelectedId(initialId);
      setInputValue(initialValue ?? '');
      setIsSelected(!!initialValue);
    },
    [initialId, initialValue],
  );

  useEffect(
    () => {
      if (!isSelected) {
        handleDeselect();
      }
    },
    [handleDeselect, isSelected],
  );

  return useMemo(
    () => ({
      initialValue,
      inputValue,
      isSelected,
      onChange: handleChange,
      onClear: handleClear,
      onDeselect: handleDeselect,
      onReset: handleReset,
      onTypeAheadSelect: handleSelect,
      selectedId,
      value,
    }),
    [
      handleChange,
      handleClear,
      handleDeselect,
      handleReset,
      handleSelect,
      initialValue,
      inputValue,
      isSelected,
      selectedId,
      value,
    ],
  );
}

export function useResizableWindow(withIsResizing = false) {
  const getWindowHeight = useCallback(
    () => window.innerHeight,
    [],
  );
  const getWindowWidth = useCallback(
    () => window.innerWidth,
    [],
  );

  const [windowSize, setWindowSize] = useState({
    height: getWindowHeight(),
    width: getWindowWidth(),
  });

  const [isResizing, setIsResizing] = useState(false);

  const resizeListener = useCallback(
    () => {
      setWindowSize({
        height: getWindowHeight(),
        width: getWindowWidth(),
      });
    },
    [getWindowHeight, getWindowWidth],
  );

  useEffect(
    () => {
      window.addEventListener(
        'resize',
        resizeListener,
        false,
      );

      return () => {
        window.removeEventListener(
          'resize',
          resizeListener,
          false,
        );
      };
    },
    [resizeListener],
  );

  useEffect(
    () => {
      if (withIsResizing) {
        setIsResizing(true);

        const timeout = setTimeout(
          () => {
            setIsResizing(false);
          },
          100,
        );

        return () => {
          clearTimeout(timeout);
        };
      }
    },
    [windowSize, withIsResizing],
  );

  return useMemo(
    () => ({
      isResizing,
      ...windowSize,
    }),
    [isResizing, windowSize],
  );
}

export function useScrollableWindow() {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [scrollDirection, setScrollDirection] = useState<ScrollDirectionY>('up');

  const getScrollPosition = useCallback(
    () => window.scrollY,
    [],
  );

  const scrollListener = useCallback(
    () => {
      const newScrollPosition = getScrollPosition();
      setScrollDirection(newScrollPosition > scrollPosition ? 'down' : 'up');
      setScrollPosition(newScrollPosition);
    },
    [getScrollPosition, scrollPosition],
  );

  useEffect(
    () => {
      window.addEventListener(
        'scroll',
        scrollListener,
        false,
      );

      return () => {
        window.removeEventListener(
          'scroll',
          scrollListener,
          false,
        );
      };
    },
    [scrollListener, scrollPosition],
  );

  return useMemo(
    () => ({
      scrollDirection,
      scrollPosition,
    }),
    [scrollDirection, scrollPosition],
  );
}

const getScrollDirection = (
  oldCoordinates: ScrollCoordinates, newCoordinates: ScrollCoordinates,
): ScrollDirection => {
  if (newCoordinates.x < oldCoordinates.x) {
    return 'left';
  }

  if (newCoordinates.x > oldCoordinates.x) {
    return 'right';
  }

  if (newCoordinates.y < oldCoordinates.y) {
    return 'up';
  }

  return 'down';
};

export function useScrollListener(el: HTMLElement | null) {
  const [scrollPosition, setScrollPosition] = useState<ScrollCoordinates>({
    x: 0,
    y: 0,
  });
  const [scrollDirection, setScrollDirection] = useState<ScrollDirection>('up');
  const [isScrolling, setIsScrolling] = useState(false);

  const getElementScrollPosition = useCallback(
    () => ({
      x: el?.scrollLeft ?? 0,
      y: el?.scrollTop ?? 0,
    }),
    [el],
  );

  const scrollListener = useCallback(
    () => {
      const newScrollPosition = getElementScrollPosition();

      setScrollDirection(getScrollDirection(
        scrollPosition,
        newScrollPosition,
      ));
      setScrollPosition(newScrollPosition);
    },
    [
      getElementScrollPosition,
      scrollPosition,
      setScrollDirection,
      setScrollPosition,
    ],
  );

  useEffect(
    () => {
      el?.addEventListener(
        'scroll',
        scrollListener,
        false,
      );

      return () => {
        el?.removeEventListener(
          'scroll',
          scrollListener,
          false,
        );
      };
    },
    [el, scrollListener],
  );

  useEffect(
    () => {
      setIsScrolling(true);

      const timeout = setTimeout(
        () => {
          setIsScrolling(false);
        },
        100,
      );

      return () => {
        clearTimeout(timeout);
      };
    },
    [scrollPosition],
  );

  return useMemo(
    () => ({
      isScrolling,
      scrollDirection,
      scrollPosition,
    }),
    [isScrolling, scrollDirection, scrollPosition],
  );
}

export function useIsOnScreen(
  ref: RefObject<HTMLElement | null>,
  hasBeenOnScreen = false,
  rootMargin = '0px',
) {
  // State and setter for storing whether element is visible
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(
    () => {
      const currentRef = ref.current;

      if (!hasBeenOnScreen) {
        const observer = new IntersectionObserver(
          ([entry]) => {
            // Update our state when observer callback fires
            setIsIntersecting(entry?.isIntersecting ?? false);
          },
          {
            rootMargin,
          },
        );
        if (currentRef) {
          observer.observe(currentRef);
        }

        return () => {
          if (currentRef) {
            observer.unobserve(currentRef);
          }
        };
      }
    },
    // Empty array ensures that effect is only run on mount and unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return isIntersecting;
}

export interface UseTextFieldOptions {
  isOnlyNumbers?: boolean;
  toUpperCase?: boolean;
}

const getUseTextFieldValue = (value: string, options?: UseTextFieldOptions) => {
  let clonedValue = value;

  if (options?.isOnlyNumbers) {
    clonedValue = clonedValue.replace(/\D/g, '');
  }

  if (options?.toUpperCase) {
    clonedValue = clonedValue.toUpperCase();
  }

  return clonedValue;
};
export function useTextField(
  initialValue = '', options?: UseTextFieldOptions,
): UseTextFieldProps {
  const [value, setValue] = useState(getUseTextFieldValue(initialValue, options));

  const handleChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      setValue(getUseTextFieldValue(evt.target.value, options));
    },
    [options],
  );

  const clear = useCallback(
    () => {
      setValue('');
    },
    [setValue],
  );

  const reset = useCallback(
    () => {
      setValue(getUseTextFieldValue(initialValue, options));
    },
    [setValue, initialValue, options],
  );

  return useMemo(
    () => ({
      clear,
      onChange: handleChange,
      reset,
      value,
    }),
    [clear, handleChange, reset, value],
  );
}

export function useOneTimeCodeField(
  initialValue = '', options?: UseTextFieldOptions,
): OneTimeCodeFieldProps {
  const textField = useTextField(
    initialValue,
    options,
  );

  return useMemo(
    () => ({
      ...textField,
      autoComplete: isMobile ? 'one-time-code' : 'off',
    }),
    [textField],
  );
}

export function usePasswordField(
  initialValue = '', options?: UsePasswordFieldOptions,
): UsePasswordFieldProps {
  const textField = useTextField(
    initialValue,
    options,
  );

  const validation = isPasswordValid(
    textField.value,
    true,
    options?.withStrengthGuide,
  );

  return useMemo<UsePasswordFieldProps>(
    () => ({
      ...textField,
      error: !validation.isValid,
      errorMessage: validation.errorMessage,
      isValid: validation.isValid,
      strengthGuide: validation.strengthGuide,
    }),
    [textField, validation.errorMessage, validation.isValid, validation.strengthGuide],
  );
}

export function useTextArea(initialValue = ''): UseTextAreaProps {
  const [value, setValue] = useState(initialValue);

  const handleChange = useCallback(
    (evt: ChangeEvent<HTMLTextAreaElement>) => {
      setValue(evt.target.value);
    },
    [],
  );

  const reset = useCallback(
    () => {
      setValue(initialValue);
    },
    [initialValue],
  );

  return useMemo(
    () => ({
      onChange: handleChange,
      reset,
      value,
    }),
    [handleChange, reset, value],
  );
}

export function useSelect(initialValue: number | string): UseSelectProps {
  const [value, setValue] = useState(initialValue);

  const handleSelect = useCallback(
    (id: number | string) => {
      setValue(id);
    },
    [],
  );

  const reset = useCallback(
    () => {
      setValue(initialValue);
    },
    [setValue, initialValue],
  );

  return useMemo(
    () => ({
      onChange: handleSelect,
      reset,
      value,
    }),
    [handleSelect, reset, value],
  );
}

function convertInitialValue(initialValue: string): string {
  if (initialValue === '') {
    return initialValue;
  }

  return getNumberFromPrice(initialValue).toFixed(2);
}

function convertValue(evtValue: string) {
  // replace any non numeric prefix values with an empty string.
  // This allows pasting values like: $10.00, USD10.00, ,10.00 all become 10
  const withoutPrefixedLetters = evtValue.replace(/^\D+/g, '');
  const withoutSuffixedLetters = withoutPrefixedLetters.replace(/\D+$/g, '');

  const centsString = withoutSuffixedLetters.replace(/\s/g, '').split('.').join('').split(',').join('');

  const centsStringNumber = Number(centsString);

  if (!isNaN(centsStringNumber)) {
    const dollars = convertCentsToDollars(centsStringNumber);

    // since we're not showing the currency symbol, the currency name
    // doesn't matter
    return formatCurrency(dollars, 'USD', { hideCurrencySymbol: true });
  }

  // since we're not showing the currency symbol, the currency name
  // doesn't matter
  return formatCurrency(0, 'USD', { hideCurrencySymbol: true });
}

export function useMoneyField(initialValue: string): UseMoneyFieldProps {
  const [value, setValue] = useState(convertValue(convertInitialValue(initialValue) || '0.00'));

  const handleChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      const moneyDisplay = convertValue(evt.target.value);
      setValue(moneyDisplay);
    },
    [],
  );

  const handleReset = useCallback(
    () => {
      setValue(convertValue(convertInitialValue(initialValue) || '0.00'));
    },
    [initialValue],
  );

  useEffect(
    () => {
      setValue(convertValue(convertInitialValue(initialValue) || '0.00'));
    },
    [initialValue],
  );

  return useMemo(
    () => ({
      onChange: handleChange,
      onReset: handleReset,
      setValue,
      value,
      valueAsNumber: getNumberFromPrice(value),
    }),
    [handleChange, handleReset, value],
  );
}

/**
 * @deprecated replace with DateInput component
 */
export const useDateField = (initialValue?: string) => {
  const [value, setValue] = useState(initialValue ? parseDateInput(initialValue) : '');

  const handleChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      setValue(parseDateInput(evt.target.value));
    },
    [],
  );

  const handleReset = useCallback(() => {
    setValue(initialValue ? parseDateInput(initialValue) : '');
  },
  [initialValue]);

  return useMemo(
    () => ({
      maxLength: 10,
      onChange: handleChange,
      onReset: handleReset,
      placeholder: 'MM/DD/YYYY',
      value,
    }),
    [handleChange, handleReset, value],
  );
};

export function useNumberOnlyField(initialValue: string): UseNumberOnlyFieldProps {
  const [value, setValue] = useState(initialValue);

  const handleChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      if (!isNaN(Number(evt.target.value))) {
        setValue(evt.target.value);
      }
    },
    [],
  );

  const reset = useCallback(
    () => {
      setValue(initialValue);
    },
    [setValue, initialValue],
  );

  return useMemo(
    () => ({
      onChange: handleChange,
      reset,
      value,
    }),
    [handleChange, reset, value],
  );
}

const alphaNumericRegex = /^[a-zA-Z0-9]+$/;

export function useAlphaNumericOnlyField(initialValue: string): UseNumberOnlyFieldProps {
  const [value, setValue] = useState(initialValue);

  const handleChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      if (alphaNumericRegex.test(evt.target.value) || evt.target.value === '') {
        setValue(evt.target.value);
      }
    },
    [],
  );

  const reset = useCallback(
    () => {
      setValue(initialValue);
    },
    [setValue, initialValue],
  );

  return useMemo(
    () => ({
      onChange: handleChange,
      reset,
      value,
    }),
    [handleChange, reset, value],
  );
}

export function usePercentOnlyField({
  initialValue,
  maximumFractionDigits = 2,
}: {
  initialValue: string;
  maximumFractionDigits?: number;
}) {
  const [value, setValue] = useState(initialValue);

  const handleChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      const [, decimal] = evt.target.value.split('.');
      const isAcceptablePrecision = !decimal?.length || decimal?.length <= maximumFractionDigits;
      if (!isNaN(Number(evt.target.value)) && isAcceptablePrecision) {
        setValue(evt.target.value);
      }
    },
    [maximumFractionDigits],
  );

  const reset = useCallback(
    () => {
      setValue(initialValue);
    },
    [setValue, initialValue],
  );

  return useMemo(
    () => ({
      onChange: handleChange,
      reset,
      value,
    }),
    [handleChange, reset, value],
  );
}

export function useViewMoreViewLess<T>({
  arr,
  buttonProps,
  maxToShow,
  seeAllText = 'See all',
  seeLessText = 'See less',
}: {
  arr: T[];
  buttonProps?: Omit<ButtonProps, 'onClick'>;
  maxToShow: number;
  seeAllText?: string;
  seeLessText?: string;
}) {
  const [showAll, setShowAll] = useState(false);

  const handleCollapse = useCallback(
    () => {
      setShowAll(false);
    },
    [],
  );

  const handleToggleShowAll = useCallback(
    () => {
      setShowAll(!showAll);
    },
    [showAll],
  );

  const getArrLength = useCallback(
    () => {
      if (showAll) {
        return arr.length;
      }

      return maxToShow;
    },
    [showAll, arr, maxToShow],
  );

  const buttonTitle = showAll ? seeLessText : seeAllText;

  return useMemo(
    () => ({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      ToggleButton: arr.length > maxToShow
        ? (
          <Button
            hierarchy="tertiary"
            onClick={handleToggleShowAll}
            {...buttonProps}
          >
            {buttonTitle}
          </Button>
        )
        : null,
      arrToShow: arr.slice(
        0,
        getArrLength(),
      ),
      handleCollapse,
    }),
    [
      arr,
      buttonProps,
      buttonTitle,
      getArrLength,
      handleCollapse,
      handleToggleShowAll,
      maxToShow,
    ],
  );
}

interface ApiHealthState {
  hasHealthStatus: boolean;
  isHealthy: boolean;
}

export function useIsApiHealthy() {
  const [apiHealthState, setApiHealthState] = useState<ApiHealthState>({
    hasHealthStatus: false,
    isHealthy: false,
  });

  useEffect(
    () => {
      const getHealthStatus = async () => {
        try {
          const response = await coreLoginClient.ping();

          setApiHealthState({
            hasHealthStatus: true,
            isHealthy: response.data.success,
          });
        } catch (_error) {
          setApiHealthState({
            hasHealthStatus: true,
            isHealthy: false,
          });
        }
      };

      void getHealthStatus();
    },
    [],
  );

  return apiHealthState;
}

export function useIsInIframe() {
  const [isInIframe, setIsInIframe] = useState(false);

  useEffect(
    () => {
      try {
        setIsInIframe(window.self !== window.top);
      } catch (_error) {
        setIsInIframe(true);
      }
    },
    [],
  );

  return useMemo(
    () => ({
      isInIframe,
      parentUrl: isInIframe ? document.referrer : null,
    }),
    [isInIframe],
  );
}
